import type { QueryFunction, QueryKey, QueryMeta, UseQueryOptions } from '@tanstack/react-query';
import type {
  EntityProjectionSpec,
  NestedEntityProjectionSpec,
} from '../core/internal/entity-projection';
import type { ViewerOverlaySpec } from '../core/internal/viewer-overlay';
import type { ViewerRelationshipSpec } from '../core/internal/viewer-relationship';
import type { JsonSerialisableArgs } from './JsonSerialisableArgs';

// ---------------------------------------------------------------------------
// T17301 · `PublicHookInput<Args, Resp>` — Row 7 closure
//
// The shape that every codegen-emitted public hook accepts. Omits the four
// override surfaces that would silently bypass the SDK's connectivity-aware
// defaults (file 05 §1440-1451):
//
//   - `staleTime`         — the function-form connectivity-aware default
//                           inside the codegen factory is the override
//                           surface. A hand-passed literal would clobber.
//   - `refetchInterval`   — same reason; the SDK chooses the interval
//                           based on `connectivity.is('CONNECTED')`.
//   - `queryKey`          — codegen-emitted shape is consumed by the
//                           reverse index, listCovers, golden tests.
//                           Overriding breaks every cache invariant.
//   - `queryFn`           — the codegen-emitted fetcher carries the
//                           realtime decode + abort signal wiring.
//
// `__unsafeStaleTime` / `__unsafeRefetchInterval` are PR-reviewed escape
// hatches (T16303 allowlist). The names are deliberately ugly so a `grep`
// finds every adoption. CODEOWNERS routes any change to the allowlist OR
// any new `__unsafe*` usage to the SDK reviewer set.
// ---------------------------------------------------------------------------

/**
 * Public input type for every codegen-emitted query hook.
 *
 * Composes:
 *   - The hook's `Args` (entity ids, filters, pagination cursor).
 *   - `Omit<UseQueryOptions, ...>` — the TanStack options surface, minus
 *     the four overrides that would bypass codegen defences.
 *   - The two PR-reviewed escape hatches: `__unsafeStaleTime`,
 *     `__unsafeRefetchInterval`. Both numeric; `'static'` is permitted
 *     for `__unsafeStaleTime` to express the "never refetch" intent
 *     (Number.POSITIVE_INFINITY) without forcing consumers to import the
 *     constant.
 *
 * @typeParam Args - the codegen'd args shape for this hook (e.g. `{ id: string }`)
 * @typeParam Resp - the codegen'd response shape (e.g. `Market`)
 */
export type PublicHookInput<Args, Resp> = Omit<
  UseQueryOptions<Resp>,
  'staleTime' | 'refetchInterval' | 'queryKey' | 'queryFn'
> & {
  /** PR-reviewed escape hatch. `'static'` is sugar for `Number.POSITIVE_INFINITY`. */
  readonly __unsafeStaleTime?: number | 'static';
  /** PR-reviewed escape hatch. Number of ms between refetches. */
  readonly __unsafeRefetchInterval?: number;
} & Args;

export type HookKind = 'detail' | 'list' | 'infinite';

/**
 * **HookSpec.mounting** · 3-state discriminant declaring how the hook is
 * mounted and whether per-entity subjects are permitted (T7407).
 *
 * - `viewport-gated` (default for market/portfolio data): hooks MUST be
 *   mounted in route components; entity subjects gated by viewport
 *   visibility.
 * - `singleton`: mounted once at a layout (e.g. top-nav balance); MAY
 *   still allow per-entity subjects via render-time params.
 * - `singleton-only`: mounted once at a layout; NEVER carries entity ids
 *   (e.g. user profile, feature flags). Lint rule T7410 permits inline
 *   selectors on this variant (small payloads only).
 *
 * Codegen and lint rules read `mounting.kind` to decide whether inline
 * selectors are allowed and whether viewport gating is mandatory.
 */
export type HookMounting =
  | { readonly kind: 'viewport-gated'; readonly perEntitySubjects: true }
  | { readonly kind: 'singleton'; readonly allowsPerEntity: boolean }
  | { readonly kind: 'singleton-only'; readonly allowsPerEntity: false };

/**
 * Exhaustiveness helper. Adding a fourth `HookMounting` variant without
 * a matching `case` here is a `tsc` error at the `never` assignment.
 */
export function mountingKind(m: HookMounting): HookMounting['kind'] {
  switch (m.kind) {
    case 'viewport-gated':
    case 'singleton':
    case 'singleton-only':
      return m.kind;
    default: {
      const _exhaustive: never = m;
      return _exhaustive;
    }
  }
}

/**
 * Branded factory return type. The `.prefix(id)` decorator (T501) is
 * mandatory on every factory consumed by a routing entry or seedsCache.
 */
export interface EntityKeyFactory<Args, _T> {
  readonly __brand: 'EntityKeyFactory';
  (args: Args): { queryKey: QueryKey; queryFn: QueryFunction<_T>; meta?: QueryMeta };
  readonly prefix: (id: string) => QueryKey;
}

/**
 * The shape every read-hook spec satisfies. Emitted by codegen from
 * OpenAPI — never hand-written. Composed at runtime by `createHook`
 * with an optional `RealtimeSpec` to produce a public hook.
 *
 * @typeParam Args - hook arguments (validated via `parseInput`). MUST be
 *                   JSON-serialisable to survive `persistQueryClient` and
 *                   cross-tab `BroadcastChannel` round-trips.
 * @typeParam T    - response shape (validated via `parseResponse`).
 */
export interface HookSpec<Args extends JsonSerialisableArgs, T> {
  readonly __brand: 'HookSpec';

  /** OpenAPI path template, e.g. `/v1/markets/{id}` */
  readonly path: string;
  readonly method: 'GET';
  /** Scheduled HTTP refreshes; independent of cache freshness. */
  readonly polling?: { readonly hasNats: boolean; readonly intervalMs?: number | false };

  /** Discriminates which factory method to invoke. */
  readonly kind: HookKind;

  /**
   * **Mounting intent** — required (no default). Drives codegen wrapper
   * selection (viewport-gated vs singleton vs singleton-only) and the
   * T7410 lint rule that permits inline selectors only on
   * `singleton-only`. See `HookMounting` for variant semantics.
   */
  readonly mounting: HookMounting;

  /** Hierarchical query-key factory; carries the queryFn for the chosen kind. */
  readonly entityKeys: {
    readonly detail?: EntityKeyFactory<Args, T>;
    readonly list?: EntityKeyFactory<Args, T>;
    readonly infinite?: EntityKeyFactory<Args, T>;
  };

  /** Args validator (codegen'd from OpenAPI request schema). Throws on parse failure. */
  readonly parseInput: (raw: unknown) => Args;

  /** Response validator (codegen'd). Throws → routes to `SdkError('schema')`. */
  readonly parseResponse: (raw: unknown) => T;

  /**
   * REQUIRED when `kind === 'list'` AND a routing entry of
   * `mode: 'patch:set' | 'invalidate'` targets this spec. Codegen rejects
   * any list spec consumed by such a routing entry without this fn.
   *
   * Returns the entity ids contained in this list's response so the
   * registry's reverse index (`Map<entityId, Set<QueryKeyHash>>`) can
   * O(1)-route per-event invalidations to affected lists.
   *
   * Wrapped via `safeExtract(spec, data)` at the call site — see file 05
   * §1767 — so a throw doesn't brick global dispatch.
   */
  readonly extractIdsForReverseIndex?: (data: T) => readonly string[];

  /**
   * Stable identifier for telemetry + devtool attribution. By convention,
   * `'useGetMarkets'`, `'useGetMarketDetail'`, etc. — matches the public
   * hook name. Codegen guarantees uniqueness across all specs.
   */
  readonly specName: string;

  /** Response varies by the effective authenticated viewer. */
  readonly viewerSensitive?: boolean;

  /** Query must not be serialized to device persistence. */
  readonly nonpersistent?: boolean;

  /**
   * MOR-859 — normalized entity store (`kind: 'list'` only, manifest
   * `normalized: true`). When true, the list caches hold `readonly string[]`
   * of row ids and each row lives once at `entityQueryKey(id)`. The runtime
   * splits fetched rows into per-entity cache entries, redirects the leaf
   * `useGet<Entity>Field` reads there, and patches the single entity (not
   * every feed copy) on a realtime event. Absent / false → the full-row
   * `PagedData<Row>` envelope, unchanged.
   */
  readonly normalized?: boolean;

  /** GC delay for normalized rows after no active list index references them.
   * Referenced ids are held by cache-only observers and cannot be collected. */
  readonly entityGcTimeMs?: number;

  /** False for immutable rows whose cached sibling list order cannot change. */
  readonly rerankSiblingFeeds?: false;

  /** True for the root detail hook paired with a normalized list. Its query
   * key is the same canonical entity entry used by every list row. */
  readonly canonicalEntity?: boolean;

  /** Public user detail projection that is materialized through the one
   * canonical public identity entity and its identifier aliases. */
  readonly publicUserEntity?: boolean;

  /** Gift list/detail response materialized into the shared network/PDA
   * entity at REST, hydration, and realtime source boundaries. */
  readonly giftEntity?: boolean;

  /** Viewer-scoped private account projection materialized separately from
   * the public identity entity. */
  readonly privateAccountEntity?: boolean;

  /** Canonical private entity shared by unrelated detail/list routes. The
   * scope arg is hashed with the provider viewer scope at runtime, so auth,
   * impersonation and wallet-set changes cannot cross-contaminate rows.
   * Omit scopeArg when provider viewer identity is the entire private scope. */
  readonly entityStore?: {
    readonly resource: string;
    readonly scopeArg?: string;
  };

  /** Builds the per-entity cache key `['<entity>','entity', id]` for a
   *  normalized list hook. Present iff `normalized` is true. */
  readonly entityQueryKey?: (id: string) => readonly unknown[];

  /** Public DTO aliases for a normalized list sharing one canonical resource
   * with differently-shaped endpoints. */
  readonly entityProjection?: EntityProjectionSpec;

  /** Nested entity rows stored as stable references on this parent entity. */
  readonly nestedEntityProjection?: NestedEntityProjectionSpec;

  /** Viewer-specific fields split away from the canonical entity. Identity
   * fields are scoped to the authenticated session; wallet fields add the
   * declared wallet argument to that scope. */
  readonly viewerOverlay?: ViewerOverlaySpec;

  /** Viewer-scoped membership fact materialized independently from the
   * endpoint projection. Point checks and management lists share this record. */
  readonly viewerRelationship?: ViewerRelationshipSpec;
}

export type AnyHookSpec = HookSpec<JsonSerialisableArgs, unknown>;

// ---------------------------------------------------------------------------
// T506 · `ReadListSpec` + `validateReadListSpec`
//
// Per-spec `extractIdsForReverseIndex(data) => readonly string[]` is declared
// in the read spec and codegen-emitted. A naive `data.map(r => r.id)` would
// throw `TypeError` on paginated `{items, cursor}`, infinite-query merged
// shapes, or specs whose id field is renamed (e.g. `wallet`, `address`).
// Codegen REQUIRES this fn on every list spec consumed by a routing entry
// with `mode: 'patch:set'` or `'invalidate'` (file 05 §1767). Runtime always
// wraps invocations via `safeExtract(spec, data)` so a throw doesn't brick
// global dispatch.
// ---------------------------------------------------------------------------

/**
 * Shape passed to `validateReadListSpec`. Looser than `HookSpec` so codegen
 * can validate intermediate spec values before they're branded.
 *
 * Note: this is a *validation contract*, not a runtime brand. Codegen emits
 * full `HookSpec` instances; this interface exists for the validator to
 * accept either the codegen IR or the final spec.
 */
export interface ReadListSpec<Args, Resp> {
  readonly name: string;
  readonly factory: {
    (args: Args): { queryKey: QueryKey; queryFn: QueryFunction<Resp> };
    readonly prefix?: (id: string) => QueryKey;
  };
  readonly extractIdsForReverseIndex: (data: Resp) => readonly string[];
  readonly listCovers?: (filters: Args, eventEntityId: string) => boolean;
}

/**
 * Validate a list spec. Returns an array of human-readable error strings;
 * empty array = valid. Used by codegen `emit-spec.ts` to reject specs whose
 * extractor is missing or non-callable before emission.
 *
 * Acceptance: a spec with `extractIdsForReverseIndex: undefined` fails the
 * check; a spec carrying a real function passes.
 */
export function validateReadListSpec(spec: {
  readonly name: string;
  readonly extractIdsForReverseIndex?: unknown;
}): readonly string[] {
  const errors: string[] = [];
  if (typeof spec.extractIdsForReverseIndex !== 'function') {
    errors.push(`list spec '${spec.name}' missing extractIdsForReverseIndex`);
  }
  return errors;
}
